import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  Typography,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  Alert,
  CircularProgress,
  MenuItem,
} from "@mui/material";
import { Edit, Add, ArrowBack, Delete } from "@mui/icons-material";

// Axios instance setup
const api = axios.create({
  baseURL: "https://attendanceapi.gensparc.com/task", // Adjust to your backend URL
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Helper function to generate a unique Task Ref Id
const generateTaskId = () => {
  return `T-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
};

const TaskPage = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [tasks, setTasks] = useState([]);
  const [selectedTasks, setSelectedTasks] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [formData, setFormData] = useState({
    task_name: "",
    task_id: "",
    task_type: "",
    occurrence: "Once",
    start_date: new Date().toISOString().slice(0, 10),
    end_date: new Date().toISOString().slice(0, 10),
    employee_id: "",
    status: "Pending",
    task_description: "",
  });
  const [errorMessage, setErrorMessage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const tasksPerPage = 10;
  const totalPages = Math.ceil(tasks.length / tasksPerPage);
  const displayedTasks = tasks.slice(
    (currentPage - 1) * tasksPerPage,
    currentPage * tasksPerPage
  );

  // Fetch tasks
  useEffect(() => {
    const fetchTasks = async () => {
      setIsLoading(true);
      setErrorMessage(null);
      try {
        const response = await api.get("/list");
        setTasks(response.data || []);
      } catch (error) {
        console.error("Fetch tasks error:", error);
        setErrorMessage(
          error.response?.data?.message || "Failed to fetch tasks."
        );
      } finally {
        setIsLoading(false);
      }
    };
    fetchTasks();
  }, []);

  // Adjust page if needed
  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [tasks, totalPages, currentPage]);

  // Fetch employees list from employee endpoint
  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(
          "https://attendanceapi.gensparc.com/employee/list",
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setEmployees(response.data || []);
      } catch (error) {
        console.error("Fetch employees error:", error);
      }
    };
    fetchEmployees();
  }, []);

  const handlePageChange = (page) => setCurrentPage(page);

  const handleSelectTask = useCallback((taskId) => {
    setSelectedTasks((prev) =>
      prev.includes(taskId)
        ? prev.filter((id) => id !== taskId)
        : [...prev, taskId]
    );
  }, []);

  // Delete tasks
  const handleDelete = useCallback(async () => {
    if (selectedTasks.length === 0) return;
    setIsLoading(true);
    setErrorMessage(null);
    try {
      await Promise.all(
        selectedTasks.map((taskId) => api.delete(`/delete/${taskId}`))
      );
      const response = await api.get("/list");
      setTasks(response.data || []);
      setSelectedTasks([]);
    } catch (error) {
      console.error("Delete tasks error:", error);
      setErrorMessage(
        error.response?.data?.message || "Failed to delete tasks."
      );
    } finally {
      setIsLoading(false);
    }
  }, [selectedTasks]);

  // Open add/edit form
  const handleAddTaskClick = (task = null) => {
    if (task) {
      // Editing an existing task; ensure dates are in "YYYY-MM-DD" format
      setFormData({
        task_name: task.task_name,
        task_id: task.task_id,
        task_type: task.task_type,
        occurrence: task.occurrence,
        start_date: new Date(task.start_date).toISOString().slice(0, 10),
        end_date: new Date(task.end_date).toISOString().slice(0, 10),
        employee_id: task.employee_id,
        status: task.status,
        task_description: task.task_description,
      });
      setEditingTask(task);
    } else {
      // Adding a new task; generate a new task ref id
      setFormData({
        task_name: "",
        task_id: generateTaskId(),
        task_type: "",
        occurrence: "Once",
        start_date: new Date().toISOString().slice(0, 10),
        end_date: new Date().toISOString().slice(0, 10),
        employee_id: "",
        status: "Pending",
        task_description: "",
      });
      setEditingTask(null);
    }
    setShowAddForm(true);
    setErrorMessage(null); // Clear previous errors
  };

  // Submit form with enhanced error handling
  const handleFormSubmit = async (e) => {
    e.preventDefault();

    // Client-side validation
    if (!formData.task_name) {
      setErrorMessage("Task name is required.");
      return;
    }
    if (new Date(formData.start_date) > new Date(formData.end_date)) {
      setErrorMessage("End date must be after start date.");
      return;
    }

    setIsLoading(true);
    setErrorMessage(null);

    try {
      console.log("Submitting task data:", formData); // Debug log
      if (editingTask) {
        await api.put(`/update/${editingTask.task_id}`, formData);
      } else {
        await api.post("/add", formData);
      }
      const response = await api.get("/list");
      setTasks(response.data || []);
      setShowAddForm(false);
      setEditingTask(null);
    } catch (error) {
      console.error("Submit task error:", error);
      const serverMessage =
        error.response?.data?.message || "Unknown server error";
      setErrorMessage(`Failed to submit task: ${serverMessage}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const commonStyles = {
    fontWeight: "bold",
    fontSize: "12px",
    whiteSpace: "nowrap",
    padding: "8px",
    textAlign: "center",
  };

  return (
    <div style={{ marginRight: "0", paddingLeft: "5px", paddingRight: "5px" }}>
      <div className="container-fluid px-0 py-5" style={{ marginRight: "0" }}>
        <Card sx={{ padding: 3, marginBottom: 3, boxShadow: 3 }}>
          <div className="d-flex justify-content-between align-items-center">
            <Typography
              variant="h4"
              component="h1"
              color="textPrimary"
              sx={{ fontWeight: 600 }}
            >
              Task Management
            </Typography>
            <div>
              <Button
                onClick={() => handleAddTaskClick()}
                variant="contained"
                color="primary"
                startIcon={<Add />}
                sx={{
                  paddingX: 2,
                  paddingY: 1,
                  fontSize: "12px",
                  borderRadius: "15px",
                  boxShadow: 2,
                  "&:hover": { boxShadow: 4 },
                  marginRight: 1,
                }}
              >
                Add Task
              </Button>
              <Button
                onClick={handleDelete}
                variant="contained"
                color="error"
                startIcon={<Delete />}
                disabled={selectedTasks.length === 0 || isLoading}
                sx={{
                  paddingX: 2,
                  paddingY: 1,
                  fontSize: "12px",
                  borderRadius: "15px",
                  boxShadow: 2,
                  "&:hover": { boxShadow: 4 },
                  marginRight: 1,
                }}
              >
                Delete
              </Button>
            </div>
          </div>
        </Card>

        {errorMessage && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorMessage}
          </Alert>
        )}

        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper} sx={{ boxShadow: 2 }}>
            <Table>
              <TableHead sx={{ backgroundColor: "#f4f5f7" }}>
                <TableRow>
                  <TableCell sx={commonStyles}>
                    <input
                      type="checkbox"
                      onChange={(e) => {
                        setSelectedTasks(
                          e.target.checked
                            ? tasks.map((task) => task.task_id)
                            : []
                        );
                      }}
                      checked={
                        selectedTasks.length > 0 &&
                        selectedTasks.length === tasks.length
                      }
                    />
                  </TableCell>
                  {[
                    "Task ID",
                    "Task Name",
                    "Employee",
                    "Status",
                    "Modified Time",
                    "Action",
                  ].map((heading, index) => (
                    <TableCell key={index} sx={commonStyles}>
                      {heading}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {displayedTasks.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} sx={commonStyles}>
                      No tasks found
                    </TableCell>
                  </TableRow>
                ) : (
                  displayedTasks.map((task) => {
                    // Look up employee from employees list
                    const employee = employees.find(
                      (emp) => emp.employee_id === task.employee_id
                    );
                    return (
                      <TableRow key={task.task_id}>
                        <TableCell sx={commonStyles}>
                          <input
                            type="checkbox"
                            checked={selectedTasks.includes(task.task_id)}
                            onChange={() => handleSelectTask(task.task_id)}
                          />
                        </TableCell>
                        <TableCell sx={commonStyles}>{task.task_id}</TableCell>
                        <TableCell sx={commonStyles}>
                          {task.task_name}
                        </TableCell>
                        <TableCell sx={commonStyles}>
                          {employee
                            ? `${employee.employee_name} (${employee.employee_id})`
                            : task.employee_id}
                        </TableCell>
                        <TableCell sx={commonStyles}>{task.status}</TableCell>
                        <TableCell sx={commonStyles}>
                          {task.modified_time || task.created_at}
                        </TableCell>
                        <TableCell sx={commonStyles}>
                          <IconButton
                            sx={{
                              backgroundColor: "#ffeb3b",
                              borderRadius: "10px",
                              "&:hover": { backgroundColor: "#fbc02d" },
                            }}
                            onClick={() => handleAddTaskClick(task)}
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <div className="d-flex justify-content-between align-items-center mt-4">
          <Typography variant="body1">
            Page {currentPage} of {totalPages}
          </Typography>
          <div>
            <Button
              variant="outlined"
              color="primary"
              disabled={currentPage === 1 || isLoading}
              onClick={() => handlePageChange(currentPage - 1)}
              sx={{
                marginRight: 1,
                borderRadius: "20px",
                paddingX: 1,
                paddingY: 0.5,
                fontSize: "12px",
              }}
            >
              Previous
            </Button>
            <Button
              variant="outlined"
              color="primary"
              disabled={currentPage === totalPages || isLoading}
              onClick={() => handlePageChange(currentPage + 1)}
              sx={{
                borderRadius: "20px",
                paddingX: 1,
                paddingY: 0.5,
                fontSize: "12px",
              }}
            >
              Next
            </Button>
          </div>
        </div>
      </div>

      {/* Add/Edit Task Dialog */}
      <Dialog
        open={showAddForm}
        onClose={() => setShowAddForm(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <IconButton onClick={() => setShowAddForm(false)}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h6">
            {editingTask ? "Edit Task" : "Add Task"}
          </Typography>
        </DialogTitle>

        <Box component="form" onSubmit={handleFormSubmit} sx={{ p: 3 }}>
          {errorMessage && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errorMessage}
            </Alert>
          )}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(2, 1fr)",
              gap: "20px",
            }}
          >
            {/* Left Column */}
            <div>
              <TextField
                fullWidth
                label="Task Name"
                name="task_name"
                value={formData.task_name}
                onChange={handleInputChange}
                margin="normal"
                required
              />
              <TextField
                fullWidth
                label="Task Ref Id"
                name="task_id"
                value={formData.task_id}
                margin="normal"
                disabled
              />
              <TextField
                fullWidth
                select
                label="Task Type"
                name="task_type"
                value={formData.task_type}
                onChange={(e) => {
                  // Clear occurrence if switching to One-Time
                  setFormData((fd) => ({
                    ...fd,
                    task_type: e.target.value,
                    occurrence:
                      e.target.value === "Recurring" ? fd.occurrence : "Once",
                  }));
                }}
                margin="normal"
              >
                <MenuItem value="One-Time">One-Time</MenuItem>
                <MenuItem value="Recurring">Recurring</MenuItem>
              </TextField>

              {formData.task_type === "Recurring" && (
                <TextField
                  fullWidth
                  select
                  label="Occurrence"
                  name="occurrence"
                  value={formData.occurrence}
                  onChange={handleInputChange}
                  margin="normal"
                  
                >
                  <MenuItem value="Daily">Daily</MenuItem>
                  <MenuItem value="Weekly">Weekly</MenuItem>
                  {/* etc. */}
                </TextField>
              )}
            </div>

            {/* Right Column */}
            <div>
              <TextField
                fullWidth
                label="Start Date"
                name="start_date"
                type="date"
                value={formData.start_date}
                onChange={handleInputChange}
                margin="normal"
                required
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                fullWidth
                label="End Date"
                name="end_date"
                type="date"
                value={formData.end_date}
                onChange={handleInputChange}
                margin="normal"
                required
                InputLabelProps={{ shrink: true }}
              />
              {/* Updated Employee Dropdown Label */}
              <TextField
                fullWidth
                select
                label="Employee"
                name="employee_id"
                value={formData.employee_id}
                onChange={handleInputChange}
                margin="normal"
              >
                {employees.map((emp) => (
                  <MenuItem key={emp.employee_id} value={emp.employee_id}>
                    {emp.employee_name} ({emp.employee_id})
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                fullWidth
                label="Task Description"
                name="task_description"
                value={formData.task_description}
                onChange={handleInputChange}
                margin="normal"
              />
            </div>
          </div>

          <DialogActions sx={{ mt: 3 }}>
            <Button variant="outlined" onClick={() => setShowAddForm(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={isLoading}
            >
              {isLoading ? "Submitting..." : editingTask ? "Update" : "Submit"}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </div>
  );
};

export default TaskPage;
